class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onCartDrawerClick = this.onCartDrawerClick.bind(this);
    this.onCartDrawerChange = this.onCartDrawerChange.bind(this);
    this.onCartDrawerKeydown = this.onCartDrawerKeydown.bind(this);
    this.onOverlayClick = this.close.bind(this);
    this.onDrawerGwpGiftAdded = this.onDrawerGwpGiftAdded.bind(this);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this.bound) {
      return;
    }

    this.bound = true;
    this.addEventListener("click", this.onCartDrawerClick);
    this.addEventListener("change", this.onCartDrawerChange);
    this.addEventListener("keydown", this.onCartDrawerKeydown);

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", this.onOverlayClick);
    }

    this.initGwpController();
  }

  disconnectedCallback() {
    if (!this.bound) {
      return;
    }

    this.bound = false;
    this.removeEventListener("click", this.onCartDrawerClick);
    this.removeEventListener("change", this.onCartDrawerChange);
    this.removeEventListener("keydown", this.onCartDrawerKeydown);

    if (this.gwpController) {
      this.gwpController.destroy();
    }

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.removeEventListener("click", this.onOverlayClick);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) {
      return;
    }

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) {
        return;
      }
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    document.body.classList.add("overflow-hidden");

    setTimeout(() => {
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay && !overlay._closeListenerBound) {
        overlay._closeListenerBound = true;
        overlay.addEventListener("click", this.close.bind(this));
      }
      this.open();
    });

    this.initGwpController();
  }

  initGwpController() {
    if (typeof window.HanacureGwpController !== "function") {
      return;
    }

    if (!this.gwpController) {
      this.gwpController = new window.HanacureGwpController({
        root: this,
        containerSelector: ".cd-gwp[data-gwp-unlock-base-cents]",
        modalSelector: ".cd-gwp-modal[data-gwp-modal]",
        openSelector: "[data-gwp-open]",
        closeSelector: "[data-gwp-close]",
        addSelector: "[data-gwp-add-variant]",
        gridSelector: "[data-gwp-grid]",
        emptyMessageClass: "cd-gwp-item__name",
        incompleteAmountTag: "b",
        giftButtonDefaultText: "Add free gift",
        giftButtonLoadingText: "Adding...",
        removeIneligibleOnInit: false,
        onGiftAdded: this.onDrawerGwpGiftAdded,
      });
    }

    this.gwpController.init();
    this.gwpController.refresh();
  }

  onDrawerGwpGiftAdded() {
    return this.fetchSections()
      .then((sections) => this.reloadDrawer(sections))
      .then(() => {
        this.closeGwpModal();
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  openGwpModal() {
    const modal = this.querySelector(".cd-gwp-modal[data-gwp-modal]");
    if (!modal) {
      return;
    }
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  closeGwpModal() {
    const modal = this.querySelector(".cd-gwp-modal[data-gwp-modal]");
    if (!modal) {
      return;
    }
    modal.hidden = true;
    document.body.style.overflow = "";
  }

  fetchSections() {
    return fetch("/?sections=cart-drawer,cart-icon-bubble", {
      headers: { Accept: "application/json" },
    }).then((response) => response.json());
  }

  reloadDrawer(sections) {
    const drawerInner = this.querySelector(".cart-drawer .drawer__inner");
    if (drawerInner && sections["cart-drawer"]) {
      const tmp = document.createElement("div");
      tmp.innerHTML = sections["cart-drawer"];
      const newInner = tmp.querySelector(".drawer__inner");
      if (newInner) {
        drawerInner.innerHTML = newInner.innerHTML;
      }

      const newCartDrawer = tmp.querySelector("cart-drawer");
      if (newCartDrawer) {
        this.classList.toggle(
          "is-empty",
          newCartDrawer.classList.contains("is-empty"),
        );
      }
    }

    const bubble = document.getElementById("cart-icon-bubble");
    if (bubble && sections["cart-icon-bubble"]) {
      const tmp2 = document.createElement("div");
      tmp2.innerHTML = sections["cart-icon-bubble"];
      const nextBubble = tmp2.querySelector(".shopify-section");
      if (nextBubble) {
        bubble.innerHTML = nextBubble.innerHTML;
      }
    }

    this.closeGwpModal();
    this.initGwpController();
    if (this.gwpController) {
      this.gwpController.refresh();
    }
  }

  cartChange(line, qty) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ line: line, quantity: qty }),
    })
      .then((response) => response.json())
      .then((cart) =>
        this.removeIneligibleGwpAfterCartChange(cart).then(() => cart),
      )
      .then(() => this.fetchSections())
      .then((sections) => this.reloadDrawer(sections));
  }

  removeLine(line) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ line: line, quantity: 0 }),
    }).then((response) => response.json());
  }

  removeIneligibleGwpAfterCartChange(cart) {
    if (!this.gwpController) {
      return Promise.resolve(false);
    }
    return this.gwpController.removeIneligibleAfterCartChange(cart);
  }

  onCartDrawerClick(event) {
    const removeButton = event.target.closest("cart-remove-button");
    if (!removeButton || !removeButton.closest("cart-drawer-items")) {
      return;
    }
    event.preventDefault();
    const line = parseInt(removeButton.getAttribute("data-index"), 10);
    if (!Number.isNaN(line)) {
      this.cartChange(line, 0);
    }
  }

  onCartDrawerChange(event) {
    const quantityInput = event.target;
    if (!quantityInput.classList.contains("quantity__input")) {
      return;
    }
    if (!quantityInput.closest("cart-drawer-items")) {
      return;
    }

    const line = parseInt(quantityInput.getAttribute("data-index"), 10);
    let qty = parseInt(quantityInput.value, 10);
    const row = quantityInput.closest("tr");
    const variantInput = row
      ? row.querySelector("cart-remove-button button[data-variant-id]")
      : null;

    if (
      variantInput &&
      this.gwpController &&
      this.gwpController.isGwpVariantId(
        variantInput.getAttribute("data-variant-id"),
      ) &&
      qty > 1
    ) {
      qty = 1;
      quantityInput.value = "1";
    }

    if (!Number.isNaN(line) && !Number.isNaN(qty) && qty >= 0) {
      this.cartChange(line, qty);
    }
  }

  onCartDrawerKeydown(event) {
    if (event.key === "Escape" && !this.gwpController) {
      this.closeGwpModal();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);
